/**
 * ノートサイドバーのツリー D&D で、ポインタ位置からドロップ先を決める純粋関数。
 *
 * 行は縦に隙間なく並ぶため、ポインタの y 座標だけで「どの行の上か」を決める
 * （横にはみ出しても判定が飛ばないようにするため x は見ない）。
 *
 * - ルートへのドロップバーの上 → `rootzone`
 * - フォルダ行の上下の端 → `folderdrag:<id>`（その行の位置へ並べ替え）
 * - フォルダ行のそれ以外 → `folder:<id>`（中に入れる。確定には滞留が必要）
 * - ページ行・下書き行の上 → その行の DnD ID（`note:<id>` / `draft:<id>`）
 * - どの行の上でもない → null（ドロップしても何もしない）
 */

interface TreeRowRect {
  top: number;
  bottom: number;
}

export interface TreeRow {
  /** フォルダ行はフォルダ ID、それ以外の行は DnD ID そのもの */
  id: string;
  rect: TreeRowRect;
}

/**
 * フォルダ行の上下端で「並べ替え」とみなす帯の最大幅（px）。
 * 「中に入れる」を優先するため細くしている（28px 行なら上下 5px ずつ）
 */
const FOLDER_REORDER_EDGE_PX = 5;

/** 帯は行の高さの 20% を上限にする（低い行で中央が潰れないように） */
const FOLDER_REORDER_EDGE_RATIO = 0.2;

const containsY = (rect: TreeRowRect, y: number): boolean =>
  y >= rect.top && y <= rect.bottom;

export const folderReorderEdge = (rect: TreeRowRect): number =>
  Math.min(
    FOLDER_REORDER_EDGE_PX,
    (rect.bottom - rect.top) * FOLDER_REORDER_EDGE_RATIO,
  );

export const pickTreeDropTarget = ({
  pointerY,
  rootBar,
  folderRows,
  itemRows,
}: {
  pointerY: number;
  rootBar: TreeRowRect | null;
  folderRows: TreeRow[];
  /** ページ行・下書き行（id は DnD ID） */
  itemRows: TreeRow[];
}): string | null => {
  if (rootBar && containsY(rootBar, pointerY)) return "rootzone";

  for (const row of folderRows) {
    if (!containsY(row.rect, pointerY)) continue;
    const edge = folderReorderEdge(row.rect);
    if (pointerY < row.rect.top + edge || pointerY > row.rect.bottom - edge) {
      return `folderdrag:${row.id}`;
    }
    return `folder:${row.id}`;
  }

  for (const row of itemRows) {
    if (containsY(row.rect, pointerY)) return row.id;
  }

  return null;
};

/** 下書きの配置計算に使う、コンテナ内の 1 行 */
interface PlacementRow {
  /** 下書き行か（下書きは並び位置の基準にしない） */
  isDraft: boolean;
  id: string;
  /** 公開済みの行の sort_order（下書きは無視される） */
  sortOrder: number;
}

/**
 * コンテナ内の並び（rows）で draftId の下書きが置かれた位置を、
 * 下書きの sort_order に変換する。
 *
 * 直後にある公開済みの行の sort_order を返す（マージ時はその行の手前に
 * 挿入される）。後ろに公開済みの行が無ければ null（= 末尾）。
 */
export const draftSortOrderAt = (
  rows: PlacementRow[],
  draftId: string,
): number | null => {
  const index = rows.findIndex((row) => row.isDraft && row.id === draftId);
  if (index < 0) return null;
  const next = rows.slice(index + 1).find((row) => !row.isDraft);
  return next ? next.sortOrder : null;
};

/**
 * 公開済みの行（sort_order 昇順）の中で、sort_order の下書きを表示する
 * 位置（挿入インデックス）を返す。null は末尾。
 */
export const draftInsertIndex = (
  publishedSortOrders: number[],
  sortOrder: number | null,
): number => {
  if (sortOrder === null) return publishedSortOrders.length;
  const index = publishedSortOrders.findIndex((order) => order >= sortOrder);
  return index < 0 ? publishedSortOrders.length : index;
};
